import { MenuItem } from "@headlessui/react";
import { CheckIcon, ComputerDesktopIcon, MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { useTheme } from "../services/ThemeProvider";

const OPTIONS = [
  { value: "light", label: "Light", Icon: SunIcon },
  { value: "dark", label: "Dark", Icon: MoonIcon },
  { value: "system", label: "System", Icon: ComputerDesktopIcon },
];

const NEXT = { light: "dark", dark: "system", system: "light" };

const byValue = (value) => OPTIONS.find((option) => option.value === value);

// Rows for use inside a Headless UI `Menu`'s `MenuItems`. Each option is a real
// `MenuItem` so ArrowUp/Down and Enter reach it (a plain <button> inside
// MenuItems is unreachable by keyboard and gets role="none" from the menu's
// tree walker).
export function ThemeMenuItems() {
  const { theme, setTheme } = useTheme();

  return (
    <>
      {OPTIONS.map(({ value, label, Icon }) => {
        const isCurrent = theme === value;
        return (
          <MenuItem key={value}>
            {({ active }) => (
              <button
                type="button"
                onClick={() => setTheme(value)}
                className={classNames(
                  active ? "bg-gray-100 dark:bg-gray-800" : "",
                  "flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300",
                )}
              >
                <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span className="flex-1 text-left">{label}</span>
                {isCurrent && (
                  <>
                    <CheckIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    <span className="sr-only"> (current)</span>
                  </>
                )}
              </button>
            )}
          </MenuItem>
        );
      })}
    </>
  );
}

export default function ThemeToggle({ compact = false, className }) {
  const { theme, setTheme } = useTheme();

  if (compact) {
    const current = byValue(theme);
    const next = byValue(NEXT[theme]);
    return (
      <button
        type="button"
        onClick={() => setTheme(next.value)}
        aria-label={`Theme: ${current.label}. Switch to ${next.label}`}
        title={`Theme: ${current.label}`}
        className={classNames(
          "rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-slate-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200",
          className,
        )}
      >
        <current.Icon className="h-5 w-5" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label="Theme"
      className={classNames("flex rounded-md bg-gray-100 p-0.5 dark:bg-gray-800", className)}
    >
      {OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => setTheme(value)}
          aria-pressed={theme === value}
          className={classNames(
            theme === value
              ? "bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-50"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200",
            "flex flex-1 items-center justify-center gap-1 rounded px-2 py-1 text-xs font-medium",
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  );
}
